import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ExtensionRelease, MeDashboard } from "@edufadder/shared-types";
import { Chip, colors, radii, spacing } from "@edufadder/ui";
import { EvidenceChip, topicLabel } from "../components/EvidenceChip.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import { compareVersions, useExtensionStatus } from "../lib/extension-status.js";
import * as s from "../styles.js";

const FEEDBACK_KIND: Record<string, string> = {
  evidence_card_helpful: "Marked a note helpful",
  evidence_card_unhelpful: "Marked a note not helpful",
  not_about_this_idea: "Reported a passage wasn't about the idea",
  alias_too_broad: "Reported a match was too broad",
  other: "Sent other feedback",
};
const FEEDBACK_STATUS: Record<string, { tone: "blue" | "neutral" | "green"; label: string }> = {
  new: { tone: "blue", label: "Received" },
  triaged: { tone: "neutral", label: "Reviewed by a curator" },
  actioned: { tone: "green", label: "Led to a change ✓" },
  dismissed: { tone: "neutral", label: "Reviewed — no change needed" },
};
const SUGGESTION_STATUS: Record<string, { tone: "blue" | "neutral" | "green"; label: string }> = {
  new: { tone: "blue", label: "Waiting for a curator" },
  accepted: { tone: "green", label: "Accepted — being researched" },
  declined: { tone: "neutral", label: "Not taken forward" },
  duplicate: { tone: "neutral", label: "Already covered" },
};

export function DashboardPage(): JSX.Element {
  const [data, setData] = useState<MeDashboard | null>(null);
  const [release, setRelease] = useState<ExtensionRelease | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ext = useExtensionStatus();

  useEffect(() => {
    api.getMyDashboard().then(setData).catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load your dashboard."));
    api.getExtensionRelease().then(setRelease).catch(() => setRelease(null));
  }, []);

  if (error) return <div style={s.wide}><p style={s.errorText}>{error}</p></div>;
  if (!data) return <div style={s.wide}><p style={s.mutedText}>Loading your dashboard…</p></div>;

  const latest = release?.latest_version;
  const outdated = Boolean(ext.version && latest && compareVersions(ext.version, latest) < 0);
  const steps: Array<{ done: boolean; title: string; action: JSX.Element }> = [
    { done: ext.installed, title: "Install the EduFadder extension", action: <Link to="/get-extension">Get it</Link> },
    { done: ext.autoDetect === "on", title: "Keep automatic detection on", action: <span>It's on after install; if you turned it off: toolbar icon → <em>Turn on automatic detection</em></span> },
    { done: data.scans_all_time > 0, title: "Sign in on the extension and read a page", action: <Link to="/practice">Try the practice page</Link> },
    { done: data.edufads_opened.length > 0, title: "Open a research note", action: <span>Click a highlight or marker on a page</span> },
    { done: data.feedback.length + data.suggestions.length > 0, title: "Help improve EduFadder", action: <Link to="/suggest">Send a suggestion</Link> },
  ];
  const doneCount = steps.filter((x) => x.done).length;

  return (
    <div style={s.wide}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: spacing(4) }}>
        <div>
          <h1 style={{ ...s.heading1, fontSize: 32, margin: 0 }}>Welcome back, {data.display_name}</h1>
          <p style={{ ...s.mutedText, fontSize: 14 }}>
            Only you can see this page. It counts pages checked while you're signed in and the general topics involved — never which pages.
          </p>
        </div>
        <Link to="/account" style={s.buttonSecondaryLink}>
          Account &amp; privacy settings
        </Link>
      </div>
      <hr style={{ ...s.goldRule, marginTop: spacing(4) }} />

      {outdated && (
        <div role="status" style={{ ...s.card, borderLeft: `6px solid ${colors.gold}`, background: colors.goldTint }}>
          <strong>EduFadder v{latest} is ready</strong> (you have v{ext.version}). Your notes are already current; this adds new
          features. <Link to="/get-extension#update">Update in 2 minutes →</Link>
        </div>
      )}

      <div className="ef-grid-2" style={{ alignItems: "start" }}>
        <section style={s.card} aria-labelledby="checklist">
          <h2 id="checklist" style={{ ...s.heading2, color: colors.brand }}>
            Getting started · {doneCount}/{steps.length}
          </h2>
          <div style={{ height: 8, background: colors.goldTint, borderRadius: radii.pill, overflow: "hidden", marginBottom: spacing(4) }}>
            <div style={{ width: `${(doneCount / steps.length) * 100}%`, height: "100%", background: colors.gold }} />
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {steps.map((st) => (
              <li key={st.title} style={{ display: "flex", gap: spacing(3), padding: `${spacing(2)} 0`, borderTop: `1px solid ${colors.border}` }}>
                <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: st.done ? colors.supportGreen : "#fff", border: `2px solid ${st.done ? colors.supportGreen : colors.border}`, color: "#fff", fontSize: 13 }}>
                  {st.done ? "✓" : ""}
                </span>
                <span>
                  <span style={{ fontWeight: 600, textDecoration: st.done ? "line-through" : "none", color: st.done ? colors.muted : colors.ink }}>
                    {st.title}
                  </span>
                  <span className="sr-only">{st.done ? " (done)" : " (to do)"}</span>
                  {!st.done && <span style={{ display: "block", fontSize: 13 }}>{st.action}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section style={s.card} aria-labelledby="activity">
          <h2 id="activity" style={{ ...s.heading2, color: colors.brand }}>Your reading, last 30 days</h2>
          <div className="ef-grid-2" style={{ gap: spacing(3) }}>
            <Stat value={data.scans_last_30_days} label="pages checked while signed in" />
            <Stat value={data.edufads_opened.reduce((n, e) => n + e.times, 0)} label="research notes opened (all time)" />
          </div>
          <h3 style={{ fontSize: 14, margin: `${spacing(4)} 0 ${spacing(2)}` }}>Topics you've come across</h3>
          {data.topics_seen.length === 0 ? (
            <p style={s.mutedText}>None yet.</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: spacing(2) }}>
              {data.topics_seen.map((t) => (
                <span key={t} style={{ ...s.chip, background: colors.goldTint }}>
                  {topicLabel(t)}
                </span>
              ))}
            </div>
          )}
          <p style={{ ...s.mutedText, marginTop: spacing(4) }}>
            Extension: {ext.installed ? `v${ext.version} installed in this browser, automatic detection ${ext.autoDetect ?? "unknown"}` : "not detected in this browser"}.
          </p>
        </section>
      </div>

      <section style={s.card} aria-labelledby="opened">
        <h2 id="opened" style={{ ...s.heading2, color: colors.brand }}>Research notes you've opened</h2>
        {data.edufads_opened.length === 0 ? (
          <p style={s.mutedText}>
            When you open a note on a page while signed in, it's saved here so you can come back to the research.{" "}
            <Link to="/edufads">Browse the library</Link>.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {data.edufads_opened.map((e) => (
              <li key={e.id} style={{ display: "flex", justifyContent: "space-between", gap: spacing(3), flexWrap: "wrap", padding: `${spacing(2)} 0`, borderTop: `1px solid ${colors.border}` }}>
                <Link to={`/edufads/${e.id}`} style={{ fontWeight: 600 }}>
                  {e.label}
                </Link>
                <span style={{ display: "flex", gap: spacing(2), alignItems: "center" }}>
                  <EvidenceChip strength={e.evidence_strength} />
                  <span style={s.mutedText}>opened {e.times}×</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section style={s.card} aria-labelledby="contrib">
        <h2 id="contrib" style={{ ...s.heading2, color: colors.brand }}>Your contributions</h2>
        <p style={{ ...s.mutedText, marginTop: 0 }}>
          Every response is read by a curator. When one leads to a change, every EduFadder user gets the improvement automatically.
        </p>
        {data.feedback.length === 0 && data.suggestions.length === 0 ? (
          <p>
            Nothing yet. Use “Was this note useful?” on any note, or <Link to="/suggest">suggest an edufad</Link>.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {data.suggestions.map((sg) => (
              <li key={sg.id} style={{ padding: `${spacing(2)} 0`, borderTop: `1px solid ${colors.border}` }}>
                <div style={{ fontWeight: 600 }}>Suggested: {sg.idea}</div>
                <Chip tone={SUGGESTION_STATUS[sg.status]?.tone ?? "neutral"}>{SUGGESTION_STATUS[sg.status]?.label ?? sg.status}</Chip>
                {sg.review_note && <div style={{ fontSize: 13, marginTop: 4 }}>Curator: {sg.review_note}</div>}
              </li>
            ))}
            {data.feedback.map((f) => (
              <li key={f.id} style={{ padding: `${spacing(2)} 0`, borderTop: `1px solid ${colors.border}` }}>
                <div>
                  {FEEDBACK_KIND[f.kind] ?? f.kind}
                  {f.claim_label ? ` — ${f.claim_label}` : ""}
                </div>
                <Chip tone={FEEDBACK_STATUS[f.status]?.tone ?? "neutral"}>{FEEDBACK_STATUS[f.status]?.label ?? f.status}</Chip>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p style={s.mutedText}>
        The library has {data.edufad_count} edufads (evidence base {data.evidence_base_version || "—"}). <Link to="/edufads">Explore it</Link> ·{" "}
        <Link to="/guide">How to use</Link>
      </p>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }): JSX.Element {
  return (
    <div style={{ background: colors.goldTint, borderRadius: radii.md, padding: spacing(3) }}>
      <p style={s.statNumber}>{value}</p>
      <p style={s.statLabel}>{label}</p>
    </div>
  );
}

